import { useCallback, useEffect, useMemo, useState } from 'react';

// Keep loaded pages while requesting only the next page, and reset on folder changes.
export default function usePagedCollection(usePageQuery, args = {}, { skip = false, pageSize = 20 } = {}) {
  const key = JSON.stringify(args);
  const [cursor, setCursor] = useState({ key, offset: 0 });
  const [cache, setCache] = useState({ key, pages: {} });
  const offset = cursor.key === key ? cursor.offset : 0;
  const query = usePageQuery({ ...args, _start: offset, _limit: pageSize }, { skip, refetchOnMountOrArgChange: true });
  const data = query.currentData;
  const { refetch } = query;
  useEffect(() => {
    if (!data || skip) return;
    setCache(previous => ({ key, pages: { ...(previous.key === key ? previous.pages : {}), [offset]: data.items } }));
  }, [data, key, offset, skip]);
  const items = useMemo(() => {
    const pages = { ...(cache.key === key ? cache.pages : {}), ...(data ? { [offset]: data.items } : {}) };
    const unique = new Map();
    Object.keys(pages).sort((a, b) => Number(a) - Number(b)).forEach(page => {
      pages[page].forEach(item => unique.set(String(item.id), item));
    });
    return [...unique.values()];
  }, [cache, key, data, offset]);
  const hasMore = !!data && (data.pagination?.hasMore ??
    (data.pagination?.total != null ? offset + data.items.length < data.pagination.total : data.items.length === pageSize));
  const loadMore = useCallback(() => {
    if (!skip && !query.isFetching && !query.isError && hasMore) {
      setCursor(current => current.key === key && current.offset > offset ? current : { key, offset: offset + pageSize });
    }
  }, [skip, query.isFetching, query.isError, hasMore, key, offset, pageSize]);
  const reload = useCallback(() => {
    setCache({ key, pages: {} });
    setCursor({ key, offset: 0 });
    if (offset === 0 && !skip) refetch();
  }, [key, offset, skip, refetch]);
  return { ...query, reload, items, hasMore, loadMore, isLoading: !skip && !data && items.length === 0 && !query.isError };
}
